import * as React from "react"
import { cn } from "@/utils/utils"
import { cva, type VariantProps } from "class-variance-authority";
import { Eye, EyeOff } from "lucide-react";

const inputVariants = cva(
  "flex h-13 w-full border bg-musio-bg-light border-musio-bg-dark px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  {
    variants: {
      variant: {
        default:
          "",
        password: 
          "pr-12", 
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>, 
  VariantProps<typeof inputVariants> {
  showPassword?: boolean;
  onTogglePasswordVisibility?: (visible: boolean) => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, showPassword: externalShowPassword, onTogglePasswordVisibility, ...props }, ref) => {
    const [internalShowPassword, setInternalShowPassword] = React.useState(false);
    
    const isPasswordVisible = externalShowPassword !== undefined ? externalShowPassword : internalShowPassword;
    
    const isPasswordType = type === "password" || variant === "password";
    const actualType = isPasswordType ? (isPasswordVisible ? "text" : "password") : type;
    
    const handleTogglePassword = React.useCallback(() => {
      const newVisibility = !isPasswordVisible;
      
      if (onTogglePasswordVisibility) {
        onTogglePasswordVisibility(newVisibility);
      } else {
        setInternalShowPassword(newVisibility);
      }
    }, [isPasswordVisible, onTogglePasswordVisibility]);

    const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
      e.preventDefault(); 
      if (!isPasswordVisible) {
        handleTogglePassword();
      }
    }, [isPasswordVisible, handleTogglePassword]);

    const handleMouseUp = React.useCallback(() => {
      if (isPasswordVisible) {
        handleTogglePassword();
      }
    }, [isPasswordVisible, handleTogglePassword]);

    const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
      e.preventDefault();
      if (!isPasswordVisible) {
        handleTogglePassword();
      }
    }, [isPasswordVisible, handleTogglePassword]);

    const handleTouchEnd = React.useCallback(() => {
      if (isPasswordVisible) {
        handleTogglePassword();
      }
    }, [isPasswordVisible, handleTogglePassword]);

    const handleMouseLeave = React.useCallback(() => {
      if (isPasswordVisible) {
        handleTogglePassword();
      }
    }, [isPasswordVisible, handleTogglePassword]);

    if (isPasswordType) {
      return (
        <div className="relative">
          <input
            type={actualType}
            className={cn(
              inputVariants({ variant }),
              className
            )}
            ref={ref}
            {...props}
          />
          <button
            type="button"
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2",
              "flex h-8 w-8 items-center justify-center",
              "text-muted-foreground hover:text-foreground",
              "transition-colors duration-150",
              "focus:outline-none focus:ring-2 focus:ring-musio-green focus:ring-offset-1",
              "rounded-md",
            )}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            tabIndex={-1}
            aria-label={isPasswordVisible ? "Hide password" : "Show password"}
          >
            {isPasswordVisible ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      );
    }

    return (
      <input
        type={type}
        className={cn(
          inputVariants({ variant }),
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

export { Input };
